/**
 * UserMenu Component
 * 
 * User avatar dropdown menu with profile links and logout.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, TrendingUp, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';

interface UserMenuProps {
  onSettingsClick?: () => void;
  onInsightsClick?: () => void;
}

export function UserMenu({ onSettingsClick, onInsightsClick }: UserMenuProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: progress } = useOnboardingProgress();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const [avatarFailed, setAvatarFailed] = useState(false);
  const handleAvatarError = useCallback(() => setAvatarFailed(true), []);

  if (!user) return null;

  // Get initials from name or email
  const getInitials = () => {
    if (user.name) {
      const parts = user.name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    setIsOpen(false);
    navigate('/');
    await logout();
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar — the avatar IS the button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-all"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        {user.avatarUrl && !avatarFailed ? (
          <img
            src={user.avatarUrl}
            alt={user.name || user.email}
            className={`w-7 h-7 rounded-full object-cover ring-1 transition-all ${
              isOpen ? 'ring-primary-500' : 'ring-primary-500/40 hover:ring-primary-500/70'
            }`}
            onError={handleAvatarError}
          />
        ) : (
          <div
            className={`w-7 h-7 rounded-full bg-primary-700 dark:bg-primary-800 flex items-center justify-center text-white text-[10px] font-bold tracking-wide ring-1 transition-all ${
              isOpen ? 'ring-primary-500' : 'ring-primary-500/40 hover:ring-primary-500/70'
            }`}
          >
            {getInitials()}
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 max-w-[calc(100vw-2rem)] md:max-w-none w-64 bg-surface-raised-light dark:bg-surface-raised-dark rounded-lg shadow-lg border border-border-subtle-light dark:border-border-subtle-dark py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-border-subtle-light dark:border-border-subtle-dark">
            <div className="font-medium text-text-primary-light dark:text-text-primary-dark truncate">
              {user.name || 'User'}
            </div>
            <div className="text-sm text-text-tertiary-light dark:text-text-secondary-dark truncate">
              {user.email}
            </div>
            {user.subscriptionTier && (
              <div className="mt-1 inline-block px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium rounded">
                {user.subscriptionTier}
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {onInsightsClick && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onInsightsClick();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                Productivity Insights
              </button>
            )}

            {onSettingsClick && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onSettingsClick();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
            )}
          </div>

          {/* Onboarding progress */}
          {progress && !progress.done && (
            <div className="px-3 py-2 border-b border-border-primary-light dark:border-border-primary-dark">
              <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark mb-1.5">
                Getting started · {progress.completed} of {progress.total}
              </p>
              <div className="h-1 bg-surface-raised-light dark:bg-surface-raised-dark rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Logout */}
          <div className="border-t border-border-subtle-light dark:border-border-subtle-dark pt-1 mt-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
